import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Copy, Download, Save } from 'lucide-react';
import { api } from '../lib/api';
import type { ModelSummary } from '../lib/api';
import { toast } from '../components/Toast';
import ModelEditor from '../components/ModelEditor';
import { currencyConfigIssues, modelCurrency } from '@rc/engine';
import type { Model } from '@rc/engine/types';

export default function ModelWorkspace() {
  const { modelId } = useParams<{ modelId?: string }>();
  const navigate = useNavigate();
  const [models, setModels] = useState<ModelSummary[]>([]);
  const [model, setModel] = useState<Model | null>(null);
  const [loading, setLoading] = useState(true);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .listModelSummaries()
      .then(setModels)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!modelId) {
      setModel(null);
      return;
    }
    api
      .getModel(modelId)
      .then((next) => {
        setModel(next);
        setDirty(false);
      })
      .catch((err) => toast('error', err.message));
  }, [modelId]);

  const updateModel = (next: Model) => {
    setModel(next);
    setDirty(true);
  };

  const handleSave = async () => {
    if (!model) return;
    // Presets must be named before the model can be saved.
    const unnamedPreset =
      (model.ogsPresets || []).some((p) => !p.name.trim()) ||
      (model.levelMixPresets || []).some((p) => !p.name.trim());
    if (unnamedPreset) {
      toast('error', 'Every saved preset needs a name before you can save the model.');
      return;
    }
    // A non-USD model with no USD rate cannot be displayed in USD or reported
    // on, so block the save rather than let it fail silently downstream.
    const currencyIssues = currencyConfigIssues(model);
    if (currencyIssues.length > 0) {
      toast('error', currencyIssues[0]!);
      return;
    }
    setSaving(true);
    try {
      const saved = await api.saveModel(model);
      setModel(saved);
      setDirty(false);
      setModels((list) => list.map((m) => (m.id === saved.id ? modelSummary(saved) : m)));
      toast('success', `Saved "${saved.name}"`);
    } catch (err) {
      toast('error', (err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const handleFork = async () => {
    if (!model) return;
    const name = prompt('Name for the forked model', `${model.name} copy`);
    if (!name) return;
    setSaving(true);
    try {
      const forked = await api.forkModel(model.id, { name });
      setModels((list) =>
        [...list, modelSummary(forked)].sort((a, b) => a.name.localeCompare(b.name)),
      );
      toast('success', `Created "${forked.name}"`);
      navigate(`/models/${forked.id}`);
    } catch (err) {
      toast('error', (err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const handleExport = () => {
    if (!model) return;
    const blob = new Blob([JSON.stringify(model, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${model.id}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!modelId) {
    return (
      <div className="px-10 py-8 max-w-6xl mx-auto">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="text-2xl font-semibold">Models</h1>
            <p className="text-slate-500 mt-1 max-w-2xl">
              Admin-managed model catalog. Open a model to edit it or fork an existing model.
            </p>
          </div>
        </div>
        {loading ? (
          <div className="card card-body">
            <div className="space-y-3">
              {[1, 2].map((i) => (
                <div key={i} className="h-12 bg-slate-100 rounded-md animate-pulse" />
              ))}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {models.map((m) => (
              <Link
                key={m.id}
                to={`/models/${m.id}`}
                className="card hover:border-brand-purple-200 hover:shadow-md transition-all p-5"
              >
                <div className="font-semibold text-slate-900">{m.name}</div>
                <div className="text-xs text-slate-500 mt-1">
                  {m.disciplineCount ?? 0} disciplines · {m.levelCount ?? 0} levels · {m.channelCount ?? 0} channels
                </div>
                {m.notes && <p className="text-xs text-slate-500 mt-2 line-clamp-2">{m.notes}</p>}
              </Link>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (!model) {
    return <div className="px-10 py-8 text-slate-500">Loading model…</div>;
  }

  return (
    <div className="flex flex-col min-h-full">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="px-8 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <Link to="/models" className="text-slate-400 hover:text-slate-700 shrink-0">
              <ArrowLeft size={18} />
            </Link>
            <div className="min-w-0">
              <div className="text-base font-semibold text-slate-900 truncate">{model.name}</div>
              <div className="text-xs text-slate-500">
                Admin editable model
                {dirty && <span className="ml-2 pill-purple">unsaved</span>}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={handleFork} className="btn-secondary" disabled={saving}>
              <Copy size={14} /> Fork
            </button>
            <button onClick={handleExport} className="btn-secondary">
              <Download size={14} /> Export
            </button>
            <button onClick={handleSave} className="btn-primary" disabled={!dirty || saving}>
              <Save size={14} /> Save
            </button>
          </div>
        </div>
      </header>
      <div className="flex-1 px-8 py-6 w-full grid grid-cols-1 xl:grid-cols-[360px_minmax(0,1fr)] gap-6">
        <section className="card">
          <div className="card-header">
            <h2 className="text-base font-semibold">Model summary</h2>
          </div>
          <div className="card-body space-y-3 text-sm">
            <SummaryRow label="ID" value={model.id} mono />
            <SummaryRow label="Disciplines" value={String(model.disciplines.length)} />
            <SummaryRow label="Levels" value={String(model.levels.length)} />
            <SummaryRow label="Channels" value={String(model.channels.length)} />
            <SummaryRow label="Currency" value={modelCurrency(model).base} />
            <SummaryRow label="Created" value={model.createdAt || '—'} />
            <SummaryRow label="Updated" value={model.updatedAt || '—'} />
            {model.notes && (
              <div>
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Notes</div>
                <p className="text-slate-700 mt-1 leading-relaxed">{model.notes}</p>
              </div>
            )}
          </div>
        </section>
        <section className="card min-w-0">
          <div className="card-header">
            <h2 className="text-base font-semibold">Raw model JSON</h2>
          </div>
          <div className="card-body">
            <pre className="max-h-[70vh] overflow-auto rounded-lg bg-slate-950 text-slate-100 text-xs leading-relaxed p-4">
              {JSON.stringify(model, null, 2)}
            </pre>
          </div>
        </section>
        <section className="xl:col-span-2 min-w-0">
          <ModelEditor model={model} onChange={updateModel} />
        </section>
      </div>
    </div>
  );
}

function modelSummary(model: Model): ModelSummary {
  return {
    id: model.id,
    name: model.name,
    parentId: model.parentId,
    version: model.version,
    engineMode: model.engineMode,
    createdAt: model.createdAt,
    updatedAt: model.updatedAt,
    notes: model.notes,
    disciplineCount: model.disciplines?.length || 0,
    channelCount: model.channels?.length || 0,
    levelCount: model.levels?.length || 0,
  };
}

function SummaryRow({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{label}</div>
      <div className={mono ? 'font-mono text-xs text-slate-700 text-right' : 'text-slate-900 text-right'}>
        {value}
      </div>
    </div>
  );
}
